import React, { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import { Calendar, X } from "lucide-react";
import { useClickOutside } from "../../hooks/useClickOutside";
import { formatDateLocal } from "./searchStorage";

export type CalendarMode = "date" | "week" | "month";

// Helper to get local midnight date
export const toLocalMidnight = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Compute 7-day week range starting from user-selected date. If maxDate is
// given (e.g. Token History can't show beyond today), the range is clipped
// so it never extends past it.
export const getWeekRange = (startDate: Date, maxDate?: Date | null) => {
  const start = toLocalMidnight(startDate);
  const effectiveMax = maxDate ? toLocalMidnight(maxDate) : null;
  const dates: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    if (effectiveMax && d > effectiveMax) break;
    dates.push(d);
  }
  const endDate = dates[dates.length - 1];
  const rangeString = `${formatDateLocal(start)} - ${formatDateLocal(endDate)}`;
  return { dates, rangeString, startDate: start, endDate };
};

// Compute month range: first day of month to last day, UNLESS a minDate was
// given (flight search can't select a past departure date) and it falls
// inside this same month — then start from minDate instead. Callers with no
// minDate restriction (e.g. Token History, which browses any date) always
// get the full 1st-to-last-day range, even for the current month.
export const getMonthRange = (
  year: number,
  monthIndex: number,
  minDate?: Date | null,
  maxDate?: Date | null,
) => {
  const firstOfMonth = new Date(year, monthIndex, 1);
  const lastOfMonth = new Date(year, monthIndex + 1, 0);

  const effectiveMin = minDate ? toLocalMidnight(minDate) : null;
  const start =
    effectiveMin &&
    effectiveMin.getFullYear() === year &&
    effectiveMin.getMonth() === monthIndex &&
    effectiveMin > firstOfMonth
      ? effectiveMin
      : firstOfMonth;

  const effectiveMax = maxDate ? toLocalMidnight(maxDate) : null;
  const endDate =
    effectiveMax &&
    effectiveMax.getFullYear() === year &&
    effectiveMax.getMonth() === monthIndex &&
    effectiveMax < lastOfMonth
      ? effectiveMax
      : lastOfMonth;
  const rangeString = `${formatDateLocal(start)} - ${formatDateLocal(endDate)}`;

  const dates: Date[] = [];
  const curr = new Date(start);
  while (curr <= endDate) {
    dates.push(new Date(curr));
    curr.setDate(curr.getDate() + 1);
  }

  return { startDate: start, endDate, rangeString, dates };
};

// Rule: Previous months cannot be selected if minDate is set (defaults to today)
// Rule: Future months cannot be selected if maxDate is set
export const isMonthDisabled = (
  year: number,
  monthIndex: number,
  minDate?: Date | null,
  maxDate?: Date | null
): boolean => {
  const effectiveMin = minDate !== undefined ? minDate : toLocalMidnight(new Date());
  if (effectiveMin) {
    const today = toLocalMidnight(effectiveMin);
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    if (year < currentYear) return true;
    if (year === currentYear && monthIndex < currentMonth) return true;
  }

  if (maxDate) {
    const max = toLocalMidnight(maxDate);
    const maxYear = max.getFullYear();
    const maxMonth = max.getMonth();

    if (year > maxYear) return true;
    if (year === maxYear && monthIndex > maxMonth) return true;
  }

  return false;
};

// Format input display text based on selected dates or date ranges
export const formatDisplayDates = (
  datesInRange: string[],
  selectedDates: Date[]
): string => {
  if (datesInRange.length === 0 && selectedDates.length === 0) {
    return "Choose Date";
  }

  const rangeOrDateStr = datesInRange[0];
  if (rangeOrDateStr && rangeOrDateStr.includes(" - ")) {
    const [startStr, endStr] = rangeOrDateStr.split(" - ");
    const dStart = new Date(`${startStr}T00:00:00`);
    const dEnd = new Date(`${endStr}T00:00:00`);

    if (!isNaN(dStart.getTime()) && !isNaN(dEnd.getTime())) {
      const sMonth = dStart.toLocaleDateString("en-US", { month: "short" }).toUpperCase();
      const eMonth = dEnd.toLocaleDateString("en-US", { month: "short" }).toUpperCase();
      const sDay = String(dStart.getDate()).padStart(2, "0");
      const eDay = String(dEnd.getDate()).padStart(2, "0");

      if (sMonth === eMonth && sDay === eDay) {
        return `${sMonth} ${sDay}`;
      }
      return `${sMonth} ${sDay} | ${eMonth} ${eDay}`;
    }
    return rangeOrDateStr;
  }

  if (datesInRange.length > 0) {
    if (datesInRange.length === 1) {
      const d = new Date(`${datesInRange[0]}T00:00:00`);
      if (!isNaN(d.getTime())) {
        const m = d.toLocaleDateString("en-US", { month: "short" }).toUpperCase();
        const day = String(d.getDate()).padStart(2, "0");
        return `${m} ${day}`;
      }
      return datesInRange[0];
    }
    if (datesInRange.length === 2 && !datesInRange[0].includes(" - ")) {
      const d1 = new Date(`${datesInRange[0]}T00:00:00`);
      const d2 = new Date(`${datesInRange[1]}T00:00:00`);
      if (!isNaN(d1.getTime()) && !isNaN(d2.getTime())) {
        const m1 = d1.toLocaleDateString("en-US", { month: "short" }).toUpperCase();
        const day1 = String(d1.getDate()).padStart(2, "0");
        const m2 = d2.toLocaleDateString("en-US", { month: "short" }).toUpperCase();
        const day2 = String(d2.getDate()).padStart(2, "0");
        return `${m1} ${day1} | ${m2} ${day2}`;
      }
    }
    return `Selected Dates (${datesInRange.length})`;
  }

  if (selectedDates.length === 1) {
    const d = selectedDates[0];
    const m = d.toLocaleDateString("en-US", { month: "short" }).toUpperCase();
    const day = String(d.getDate()).padStart(2, "0");
    return `${m} ${day}`;
  }

  if (selectedDates.length === 2) {
    const d1 = selectedDates[0];
    const d2 = selectedDates[1];
    const m1 = d1.toLocaleDateString("en-US", { month: "short" }).toUpperCase();
    const day1 = String(d1.getDate()).padStart(2, "0");
    const m2 = d2.toLocaleDateString("en-US", { month: "short" }).toUpperCase();
    const day2 = String(d2.getDate()).padStart(2, "0");
    return `${m1} ${day1} | ${m2} ${day2}`;
  }

  if (selectedDates.length > 2) {
    return `Selected Dates (${selectedDates.length})`;
  }

  return "Choose Date";
};

interface DateRangeCalendarProps {
  selectedDates: Date[];
  datesInRange: string[];
  isCalendarOpen: boolean;
  setIsCalendarOpen: (open: boolean) => void;
  onDateChange: (date: Date | null) => void;
  onSaveDates: (
    overrideDatesInRange?: string[],
    overrideSelectedDates?: Date[]
  ) => void;
  onClearDates: () => void;
  error?: string;
  popperPlacement?: any;
  portalId?: string;
  minDate?: Date | null;
  maxDate?: Date | null;
  className?: string;
  /** Flight Status only supports single-day lookup (Yesterday/Today/Tomorrow). Set false to hide the Week/Month tabs. */
  showModeSwitcher?: boolean;
}

/* Popover is scaled to 85% via transform; font-sizes below are pre-compensated
   (divided by 0.85) so the rendered text stays visually the same size. */
const POPOVER_SCALE = 0.85;

const compactCalendarStyles = `
  .date-range-calendar-container .react-datepicker {
    font-size: 1rem !important;
    border: none !important;
    font-family: inherit !important;
    background-color: transparent !important;
    width: 100% !important;
  }
  .date-range-calendar-container .react-datepicker__month-container {
    float: none !important;
    width: 100% !important;
  }
  .date-range-calendar-container .react-datepicker__header {
    background-color: transparent !important;
    border-bottom: none !important;
    padding: 0 !important;
  }
  .date-range-calendar-container .react-datepicker__day-names {
    display: flex !important;
    justify-content: space-around !important;
    margin-bottom: 5px !important;
    margin-top: 3px !important;
    padding-bottom: 7px !important;
    border-bottom: 1px solid #ECECED !important;
  }
  .date-range-calendar-container .react-datepicker__day-name {
    color: #7E818C !important;
    font-weight: 500 !important;
    font-size: 0.8824rem !important;
    width: 2.1rem !important;
    height: 1.62rem !important;
    line-height: 1.62rem !important;
    margin: 0 !important;
    text-align: center !important;
    text-transform: capitalize !important;
  }
  .date-range-calendar-container .react-datepicker__week {
    display: flex !important;
    justify-content: space-around !important;
    margin-bottom: 5px !important;
  }
  .date-range-calendar-container .react-datepicker__day {
    width: 2.1rem !important;
    height: 1.89rem !important;
    line-height: 1.89rem !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    margin: 0 !important;
    font-size: 1rem !important;
    font-weight: 500 !important;
    color: #0F2552 !important;
    border-radius: 50% !important;
    box-sizing: border-box !important;
    transition: all 0.15s ease !important;
  }
  .date-range-calendar-container .react-datepicker__day--keyboard-selected:not(.react-datepicker__day--highlighted) {
    background-color: transparent !important;
    color: #0F2552 !important;
    font-weight: 500 !important;
  }
  .date-range-calendar-container .react-datepicker__day--today:not(.react-datepicker__day--highlighted) {
    background-color: transparent !important;
    color: #0F2552 !important;
    font-weight: 600 !important;
  }
  .date-range-calendar-container .react-datepicker__day--highlighted,
  .date-range-calendar-container .react-datepicker__day--selected {
    background-color: #030405 !important;
    color: #FFFFFF !important;
    font-weight: 700 !important;
    border-radius: 50% !important;
  }
  .date-range-calendar-container .react-datepicker__day--disabled {
    color: #94A3B8 !important;
    opacity: 0.75 !important;
    cursor: not-allowed !important;
    background-color: transparent !important;
  }
  .date-range-calendar-container .react-datepicker__day--outside-month {
    visibility: hidden !important;
    pointer-events: none !important;
  }
  .date-range-calendar-container .react-datepicker__day:hover:not(.react-datepicker__day--disabled) {
    background-color: #F3F4F6 !important;
    color: #000000 !important;
    border-radius: 50% !important;
  }
  .date-range-calendar-container .react-datepicker__day--highlighted:hover:not(.react-datepicker__day--disabled) {
    background-color: #18181B !important;
    color: #FFFFFF !important;
  }
`;

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const DateRangeCalendar: React.FC<DateRangeCalendarProps> = ({
  selectedDates,
  datesInRange,
  isCalendarOpen,
  setIsCalendarOpen,
  onDateChange,
  onSaveDates,
  onClearDates,
  error,
  minDate,
  maxDate,
  className,
  showModeSwitcher = true,
}) => {
  const [mode, setMode] = useState<CalendarMode>("date");
  const [localSelectedDates, setLocalSelectedDates] =
    useState<Date[]>(selectedDates);
  const [localDatesInRange, setLocalDatesInRange] =
    useState<string[]>(datesInRange);
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [selectedMonthIndex, setSelectedMonthIndex] = useState<number | null>(
    null
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  const prevOpenRef = useRef<boolean>(false);
  const currentYear = new Date().getFullYear();
  const effectiveMinDate = minDate !== undefined ? minDate : toLocalMidnight(new Date());
  const effectiveMaxDate = maxDate !== undefined ? maxDate : null;

  useClickOutside(containerRef, () => setIsCalendarOpen(false));

  /* On narrow screens the popover is anchored to the trigger's right edge, so it
     can extend past the left edge of the viewport. Measure once it opens and
     shift it horizontally so it always stays fully visible. */
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [popoverShift, setPopoverShift] = useState(0);

  useEffect(() => {
    if (!isCalendarOpen) {
      setPopoverShift(0);
      return;
    }

    const GUTTER = 8;

    const adjust = () => {
      const el = popoverRef.current;
      if (!el) return;
      // Measure without any previously applied shift.
      const rect = el.getBoundingClientRect();
      const left = rect.left - popoverShift;
      const right = rect.right - popoverShift;
      let shift = 0;
      if (left < GUTTER) {
        shift = GUTTER - left;
      } else if (right > window.innerWidth - GUTTER) {
        shift = window.innerWidth - GUTTER - right;
      }
      setPopoverShift(shift);
    };

    adjust();
    window.addEventListener("resize", adjust);
    return () => window.removeEventListener("resize", adjust);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCalendarOpen]);

  // Compute default selected dates/ranges for mode switching
  const getDefaultStateForMode = (
    targetMode: CalendarMode
  ): { dates: Date[]; ranges: string[]; monthIdx: number | null } => {
    const today = toLocalMidnight(new Date());
    if (targetMode === "date") {
      return { dates: [today], ranges: [formatDateLocal(today)], monthIdx: null };
    }
    if (targetMode === "week") {
      const week = getWeekRange(today, effectiveMaxDate);
      return {
        dates: week.dates,
        ranges: [week.rangeString],
        monthIdx: null,
      };
    }
    if (targetMode === "month") {
      const currYr = today.getFullYear();
      const currMIdx = today.getMonth();
      const month = getMonthRange(currYr, currMIdx, effectiveMinDate, effectiveMaxDate);
      return {
        dates: month.dates,
        ranges: [month.rangeString],
        monthIdx: currMIdx,
      };
    }
    return { dates: [], ranges: [], monthIdx: null };
  };

  // Sync state ONLY when calendar opens (transitions from closed to open)
  useEffect(() => {
    const justOpened = isCalendarOpen && !prevOpenRef.current;
    prevOpenRef.current = isCalendarOpen;

    if (justOpened) {
      if (selectedDates.length > 0 || datesInRange.length > 0) {
        setLocalSelectedDates(selectedDates);
        setLocalDatesInRange(datesInRange);

        // Detect saved mode from datesInRange
        if (datesInRange.length > 0 && datesInRange[0].includes(" - ")) {
          const [startStr, endStr] = datesInRange[0].split(" - ");
          const dStart = new Date(`${startStr}T00:00:00`);
          const dEnd = new Date(`${endStr}T00:00:00`);
          if (!isNaN(dStart.getTime()) && !isNaN(dEnd.getTime())) {
            const sDay = dStart.getDate();
            const eDay = dEnd.getDate();
            const lastDayOfM = new Date(
              dEnd.getFullYear(),
              dEnd.getMonth() + 1,
              0
            ).getDate();

            if (
              (sDay === 1 || sDay === new Date().getDate()) &&
              eDay === lastDayOfM
            ) {
              setMode("month");
              setYear(dStart.getFullYear());
              setSelectedMonthIndex(dStart.getMonth());
              return;
            }

            const diffDays = Math.round(
              (dEnd.getTime() - dStart.getTime()) / (1000 * 3600 * 24)
            );
            if (diffDays === 6) {
              setMode("week");
              setSelectedMonthIndex(null);
              return;
            }
          }
        }

        setMode("date");
        setSelectedMonthIndex(null);
      } else {
        // No date selected yet: default Date mode with current date (today) selected!
        setMode("date");
        const defaults = getDefaultStateForMode("date");
        setLocalSelectedDates(defaults.dates);
        setLocalDatesInRange(defaults.ranges);
        setSelectedMonthIndex(null);
      }
    }
  }, [isCalendarOpen, selectedDates, datesInRange]);

  // Tab switcher handler: sets default current date/week/month when switching modes
  const handleModeChange = (newMode: CalendarMode) => {
    if (newMode === mode) return;
    setMode(newMode);
    const defaults = getDefaultStateForMode(newMode);
    setLocalSelectedDates(defaults.dates);
    setLocalDatesInRange(defaults.ranges);
    setSelectedMonthIndex(defaults.monthIdx);
    if (newMode === "month") {
      setYear(new Date().getFullYear());
    }
  };

  // Date selection handler (Date mode)
  const handlePickerDateSelect = (date: Date | null) => {
    if (!date) return;
    if (effectiveMinDate && toLocalMidnight(date).getTime() < toLocalMidnight(effectiveMinDate).getTime()) return;
    if (effectiveMaxDate && toLocalMidnight(date).getTime() > toLocalMidnight(effectiveMaxDate).getTime()) return;
    const dateStr = formatDateLocal(date);
    const dateIndex = localSelectedDates.findIndex(
      (d) => formatDateLocal(d) === dateStr
    );

    let newDates: Date[];
    if (dateIndex > -1) {
      newDates = localSelectedDates.filter((_, index) => index !== dateIndex);
    } else {
      newDates = [...localSelectedDates, date].sort(
        (a, b) => a.getTime() - b.getTime()
      );
    }

    setLocalSelectedDates(newDates);
    setLocalDatesInRange(newDates.map((d) => formatDateLocal(d)));
    setSelectedMonthIndex(null);
  };

  // Week selection handler (Week mode: 7 days from selected date)
  const handleWeekDateSelect = (startDate: Date | null) => {
    if (!startDate) return;
    if (effectiveMinDate && toLocalMidnight(startDate).getTime() < toLocalMidnight(effectiveMinDate).getTime()) return;
    if (effectiveMaxDate && toLocalMidnight(startDate).getTime() > toLocalMidnight(effectiveMaxDate).getTime()) return;
    const week = getWeekRange(startDate, effectiveMaxDate);
    setLocalSelectedDates(week.dates);
    setLocalDatesInRange([week.rangeString]);
    setSelectedMonthIndex(null);
  };

  // Month selection handler (Month mode: selects days in that month)
  const handleMonthSelect = (monthIdx: number) => {
    if (isMonthDisabled(year, monthIdx, effectiveMinDate, effectiveMaxDate)) return;
    setSelectedMonthIndex(monthIdx);
    const month = getMonthRange(year, monthIdx, effectiveMinDate, effectiveMaxDate);
    setLocalSelectedDates(month.dates);
    setLocalDatesInRange([month.rangeString]);
  };

  // Clear all handler (resets calendar selections to empty)
  const handleClearAll = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setLocalSelectedDates([]);
    setLocalDatesInRange([]);
    setSelectedMonthIndex(null);
    onClearDates();
  };

  // Done button handler (confirms selection and closes dropdown)
  const handleDone = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onSaveDates(localDatesInRange, localSelectedDates);
    setIsCalendarOpen(false);
  };

  const activeDatesInRange = isCalendarOpen ? localDatesInRange : datesInRange;
  const activeSelectedDates = isCalendarOpen ? localSelectedDates : selectedDates;
  const displayText = formatDisplayDates(activeDatesInRange, activeSelectedDates);

  return (
    <div
      ref={containerRef}
      className={`relative flex-1 ${
        className ? className : "h-[56px] border border-[#ECECED] bg-white rounded-2xl"
      } cursor-pointer transition-colors hover:bg-gray-50 flex flex-col justify-center px-3 sm:px-4 min-w-0`}
      onClick={() => {
        if (!isCalendarOpen) {
          setIsCalendarOpen(true);
        }
      }}
    >
      <style>{compactCalendarStyles}</style>

      {datesInRange.length > 0 || selectedDates.length > 0 ? (
        /* Selected Date State: Stacked sub-header with calendar icon + label above selected date string */
        <div
          className={`flex flex-col justify-center min-w-0 py-1 ${
            error ? "" : "h-full"
          }`}
        >
          <div className="flex items-center gap-1 text-[#6B7280] mb-0.5">
            <svg
              className="w-[13px] h-[13px] text-[#6B7280] shrink-0 inline-block"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="6" width="18" height="15" rx="3.5" ry="3.5" />
              <path d="M8 3.5v3.5" />
              <path d="M16 3.5v3.5" />
              <path d="M3 11h18" />
            </svg>
            <span className="text-[11px] sm:text-xs font-semibold font-inter text-[#6B7280] leading-none">
              Date
            </span>
          </div>

          <div className="relative flex-1 flex items-center justify-between min-w-0">
            {displayText.includes(" | ") ? (
              <div className="flex items-center gap-1.5 text-base sm:text-[17px] font-semibold font-inter text-[#111827] leading-tight truncate">
                <span>{displayText.split(" | ")[0]}</span>
                <span className="text-gray-300 font-normal">|</span>
                <span>{displayText.split(" | ")[1]}</span>
              </div>
            ) : (
              <span className="text-base sm:text-[17px] font-semibold font-inter text-[#111827] leading-tight truncate">
                {displayText === "Choose Date" ? "Date" : displayText}
              </span>
            )}

            <button
              onClick={(e) => {
                e.stopPropagation();
                onClearDates();
              }}
              className="p-1 text-gray-400 hover:text-gray-600 rounded-md focus:outline-none ml-1 shrink-0"
              type="button"
              title="Clear dates"
            >
              {/* <i className="st-close text-xs"></i> */}
            </button>
          </div>
        </div>
      ) : (
        /* Unselected State: Single horizontal row with icon on left */
        <div
          className={`flex items-center gap-2 min-w-0 ${error ? "" : "h-full"}`}
        >
          <div className="shrink-0 flex items-center justify-center">
            <svg
              className="w-[22px] h-[22px] text-[#6B7280] inline-block"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="6" width="18" height="15" rx="3.5" ry="3.5" />
              <path d="M8 3.5v3.5" />
              <path d="M16 3.5v3.5" />
              <path d="M3 11h18" />
              <circle cx="7.5" cy="14.5" r="0.9" fill="currentColor" stroke="none" />
              <circle cx="12" cy="14.5" r="0.9" fill="currentColor" stroke="none" />
              <circle cx="16.5" cy="14.5" r="0.9" fill="currentColor" stroke="none" />
              <circle cx="7.5" cy="18" r="0.9" fill="currentColor" stroke="none" />
              <circle cx="12" cy="18" r="0.9" fill="currentColor" stroke="none" />
              <circle cx="16.5" cy="18" r="0.9" fill="currentColor" stroke="none" />
            </svg>
          </div>

          <div className="relative flex-1 flex items-center justify-between min-w-0">
            <span className="text-base text-[#6B7280] font-normal font-inter leading-snug truncate">
              Date
            </span>
          </div>
        </div>
      )}

      {/* Validation error text inside the field, below the date line */}
      {error && (
        <p
          title={error}
          className="mt-1 text-[10px] sm:text-[11px] text-red-500 font-medium leading-tight truncate pointer-events-none"
        >
          {error}
        </p>
      )}

      {/* Compact Calendar Dropdown Popover */}
      {isCalendarOpen && (
        <div
          ref={popoverRef}
          className="absolute top-full right-0 mt-2 z-50 bg-white rounded-[19px] shadow-[9px_4px_26px_12px_rgba(150,150,150,0.11)] w-[min(315px,calc(100vw-16px))] sm:w-[330px] overflow-hidden text-left cursor-default animate-in fade-in slide-in-from-top-2 duration-150 date-range-calendar-container"
          style={{
            transform: `translateX(${popoverShift}px) scale(${POPOVER_SCALE})`,
            transformOrigin: "top right",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-3.5 sm:p-4">
            {/* Mode Switcher Pill + Close Button */}
            <div className="flex justify-center items-center gap-2 mb-2.5">
              {showModeSwitcher && (
              <div className="flex bg-[#1B1B1A] p-1 rounded-full items-center gap-0.5">
                {(
                  [
                    { id: "date", label: "Date" },
                    { id: "week", label: "Week" },
                    { id: "month", label: "Month" },
                  ] as const
                ).map((item) => {
                  const isActive = mode === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => handleModeChange(item.id)}
                      className={`py-1.5 px-3 rounded-full transition-all flex items-center justify-center gap-1.5 ${
                        isActive
                          ? "text-black font-bold shadow-sm"
                          : "text-white hover:text-gray-200 font-medium"
                      }`}
                      style={{
                        fontSize: `${12 / POPOVER_SCALE}px`,
                        ...(isActive
                          ? {
                              backgroundImage:
                                "linear-gradient(181deg, #FDC73C 2.7%, #FFBC00 82.3%)",
                            }
                          : undefined),
                      }}
                    >
                      <Calendar
                        className={`w-3.5 h-3.5 shrink-0 ${
                          isActive ? "text-black" : "text-white"
                        }`}
                      />
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>
              )}
              {showModeSwitcher && (
              <button
                type="button"
                onClick={() => setIsCalendarOpen(false)}
                className="w-8 h-8 rounded-lg bg-[#192227] text-white hover:bg-[#2C3A42] transition-colors flex items-center justify-center shrink-0"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
              )}
            </div>

          {/* Calendar Content Area */}
          <div className="bg-white">
            {mode === "date" && (
              <div className="flex justify-center">
                <DatePicker
                  inline
                  selected={null}
                  onChange={handlePickerDateSelect}
                  minDate={effectiveMinDate || undefined}
                  maxDate={effectiveMaxDate || undefined}
                  calendarStartDay={1}
                  formatWeekDay={(nameOfDay) => nameOfDay.substring(0, 3)}
                  highlightDates={localSelectedDates}
                  renderCustomHeader={({
                    date,
                    decreaseMonth,
                    increaseMonth,
                    prevMonthButtonDisabled,
                    nextMonthButtonDisabled,
                  }) => (
                    <div className="flex items-center justify-center gap-3 py-0.5 mb-0.5">
                      <button
                        type="button"
                        onClick={decreaseMonth}
                        disabled={prevMonthButtonDisabled}
                        className="p-1 text-[#3B4B68] hover:text-black disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                        aria-label="Previous month"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2.5"
                            d="M15 19l-7-7 7-7"
                          />
                        </svg>
                      </button>
                      <span
                        className="font-semibold text-[#0F2552] tracking-tight"
                        style={{ fontSize: `${14 / POPOVER_SCALE}px` }}
                      >
                        {date.toLocaleDateString("en-US", {
                          month: "long",
                          year: "numeric",
                        })}
                      </span>
                      <button
                        type="button"
                        onClick={increaseMonth}
                        disabled={nextMonthButtonDisabled}
                        className="p-1 text-[#3B4B68] hover:text-black disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                        aria-label="Next month"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2.5"
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </button>
                    </div>
                  )}
                />
              </div>
            )}

            {mode === "week" && (
              <div className="flex justify-center">
                <DatePicker
                  inline
                  selected={null}
                  onChange={handleWeekDateSelect}
                  minDate={effectiveMinDate || undefined}
                  maxDate={effectiveMaxDate || undefined}
                  calendarStartDay={1}
                  formatWeekDay={(nameOfDay) => nameOfDay.substring(0, 3)}
                  highlightDates={localSelectedDates}
                  renderCustomHeader={({
                    date,
                    decreaseMonth,
                    increaseMonth,
                    prevMonthButtonDisabled,
                    nextMonthButtonDisabled,
                  }) => (
                    <div className="flex items-center justify-center gap-3 py-0.5 mb-0.5">
                      <button
                        type="button"
                        onClick={decreaseMonth}
                        disabled={prevMonthButtonDisabled}
                        className="p-1 text-[#3B4B68] hover:text-black disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                        aria-label="Previous month"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2.5"
                            d="M15 19l-7-7 7-7"
                          />
                        </svg>
                      </button>
                      <span
                        className="font-semibold text-[#0F2552] tracking-tight"
                        style={{ fontSize: `${14 / POPOVER_SCALE}px` }}
                      >
                        {date.toLocaleDateString("en-US", {
                          month: "long",
                          year: "numeric",
                        })}
                      </span>
                      <button
                        type="button"
                        onClick={increaseMonth}
                        disabled={nextMonthButtonDisabled}
                        className="p-1 text-[#3B4B68] hover:text-black disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                        aria-label="Next month"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2.5"
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </button>
                    </div>
                  )}
                />
              </div>
            )}

            {mode === "month" && (
              <div className="py-1.5">
                {/* Year Navigation */}
                <div className="flex items-center justify-center gap-6 py-0.5 mb-2.5">
                  <button
                    type="button"
                    onClick={() => setYear((y) => Math.max(currentYear, y - 1))}
                    disabled={effectiveMinDate ? year <= currentYear : false}
                    className="p-1 text-[#3B4B68] hover:text-black disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2.5"
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                  <span
                    className="font-semibold text-[#0F2552] tracking-tight"
                    style={{ fontSize: `${14 / POPOVER_SCALE}px` }}
                  >
                    {year}
                  </span>
                  <button
                    type="button"
                    onClick={() => setYear((y) => y + 1)}
                    disabled={effectiveMaxDate ? year >= effectiveMaxDate.getFullYear() : false}
                    className="p-1 text-[#3B4B68] hover:text-black disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2.5"
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>

                {/* Month Grid */}
                <div className="grid grid-cols-3 gap-1.5">
                  {MONTH_NAMES.map((mName, idx) => {
                    const disabled = isMonthDisabled(year, idx, effectiveMinDate, effectiveMaxDate);
                    const isSelected = selectedMonthIndex === idx;

                    return (
                      <button
                        key={mName}
                        type="button"
                        disabled={disabled}
                        onClick={() => handleMonthSelect(idx)}
                        className={`py-2 rounded-xl transition-all ${
                          isSelected
                            ? "bg-black text-white font-bold shadow-sm"
                            : disabled
                            ? "text-[#94A3B8] cursor-not-allowed bg-gray-50/80 font-medium"
                            : "text-[#1E2B45] hover:bg-gray-100 font-semibold"
                        }`}
                        style={{ fontSize: `${12 / POPOVER_SCALE}px` }}
                      >
                        {mName}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Bottom Action Footer */}
          <div className="flex items-center justify-between gap-3 pt-2.5 mt-1.5 border-t border-[#ECECED]">
            <button
              onClick={handleClearAll}
              className="flex-1 py-2 px-4 font-medium text-[#192227] bg-[rgba(200,200,200,0.15)] hover:bg-[#EAEAEA] rounded-[10px] border border-[#ECECED] transition-colors text-center cursor-pointer"
              style={{ fontSize: `${13 / POPOVER_SCALE}px` }}
              type="button"
            >
              Clear all
            </button>
            <button
              onClick={handleDone}
              className="flex-1 py-2 px-4 font-medium text-[#192227] rounded-[10px] shadow-sm transition-opacity hover:opacity-90 text-center border-0 cursor-pointer"
              style={{
                fontSize: `${13 / POPOVER_SCALE}px`,
                backgroundImage:
                  "linear-gradient(121.5deg, #FEBF03 1.47%, #FFCB3A 80.19%)",
              }}
              type="button"
            >
              Done
            </button>
          </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeCalendar;
